//initial redux
import { put, call, takeEvery, fork, all, take, select, takeLatest } from "redux-saga/effects";
import * as router from "react-router-redux";
import { eventChannel, delay } from "redux-saga";
import reportsservice from "../../../../services/reports";
import moment from "moment-timezone";
import pointsservice from "../../../../services/points";
import { types as sitetreetypes } from "../../../../services/SiteTree/redux";
import ticketservice from "../../../../services/ticket";
import pointName from "../../../../utils/pointName";
import unitsservice from "../../../../utils/units";
import steppedSeries from "../../../../utils/steppedSeries";
import { DateTime, number, seriesColor } from "../../../../utils";
import { actions as seriesColorActions } from "../../../../services/SeriesColor/redux";

const LIVE_DATA_INTERVAL = 1000;

const initialState = {
	instances: {},
	isBusy: false,
	errors: [],
	hasError: false
};

/////////
//types
export const types = {
	START_LIVE_DATA: "components/reportlivelinechart/START_LIVE_DATA",
	UPDATE_LIVE_DATA: "components/reportlivelinechart/UPDATE_LIVE_DATA",
	STOP_LIVE_DATA: "components/reportlivelinechart/STOP_LIVE_DATA",
	UPDATE_LAST_UPDATED: "components/reportlivelinechart/UPDATE_LAST_UPDATED"
};

function startLiveData(state, action) {
	const id = action.payload.id;
	const chart = action.payload.chart;
	const rootView = action.payload.rootView;
	let liveLineData = {
		id,
		chart,
		rootView,
		lastUpdate: null
	};
	return { ...state, instances: { ...state.instances, [id]: liveLineData } };
}

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.START_LIVE_DATA: {
				return startLiveData(state, action);
			}
			case types.STOP_LIVE_DATA: {
				let rest = { ...state.instances };
				delete rest[action.payload.id];
				return { ...state, instances: { ...rest } };
			}
			case types.UPDATE_LAST_UPDATED: {
				return {
					...state,
					instances: {
						...state.instances,
						[action.payload.id]: {
							...state.instances[action.payload.id],
							lastUpdate: action.payload.lastUpdate
						}
					}
				};
			}
			case types.UPDATE_LIVE_DATA: {
				return {
					...state,
					instances: {
						...state.instances,
						[action.payload.id]: {
							...state.instances[action.payload.id],
							...action.payload.data
						}
					}
				};
			}
			default:
				return state;
		}
	}
};

//store live values and redraw every 1 sec. fetch new data every 5 secs
let grottyhacks = {};

function* updateInstantaneousData(config, now) {
	if (config.view.name.toLowerCase().indexOf("ticket") !== -1) {
		let sitePropName = "site_" + currentSite.name.substring("Site_".length);
		if (!grottyhacks[sitePropName]) {
			grottyhacks[sitePropName] = { count: 0, lastVal: null };
		}
		const currentSite = yield select(x => x.sitetree.currentSite);

		//get data every 5 intervals
		if (grottyhacks[sitePropName].count % 5 === 0) {
			const ticketSales = yield call(ticketservice.GetLiveSnapshot, [currentSite.name]);
			if (ticketSales) {
				grottyhacks[sitePropName].lastVal = ticketSales;
			}
		}
		grottyhacks[sitePropName].count++;
		
		if (grottyhacks[sitePropName].lastVal) {
			if (grottyhacks[sitePropName].lastVal[sitePropName]) {
				let utcOffset = 0;
				if (currentSite && currentSite.momentTimezone) {
					utcOffset = moment()
						.tz(currentSite.momentTimezone)
						.utcOffset();
				}

				if (grottyhacks[sitePropName].lastVal[sitePropName]) {
					try {
						config.series.addPoint([now.valueOf(), grottyhacks[sitePropName].lastVal[sitePropName]]);
					} catch (e) {
						console.log(e);
					}
				}
			}
		}
	} else {
		if (!grottyhacks[config.view.objectID]) {
			grottyhacks[config.view.objectID] = { count: 0, lastVal: null };
		}

		//get data every 5 intervals
		if (grottyhacks[config.view.objectID].count % 5 === 0) {
			const viewData = yield call(pointsservice.LiveDataGet, config.view.objectID);
			if (viewData && viewData[config.view.objectID]) {
				grottyhacks[config.view.objectID].lastVal = viewData[config.view.objectID];
			}
		}
		grottyhacks[config.view.objectID].count++; 

		if (grottyhacks[config.view.objectID].lastVal) {
			let data = grottyhacks[config.view.objectID].lastVal;
			try {
				if (data && !isNaN(data.value) && data.timestamp) {
					config.series.addPoint([now.valueOf(), data.value]);
				}
			} catch (e) {
				console.log(e);
			}
		}
	}
}

function* addSeries(chart, view) {
	let symbol = unitsservice.stringToSymbol(view.units);
	let isSteppedSeries = steppedSeries.isSteppedSeries(view.name, view.description);

	const colors = seriesColor.getSeriesColors();

	let {colorDictionary, colorIndex} = yield select(x => x.seriesColor);

	var pn = view.name || view.pointName;
	let color = colorDictionary[pn] ? colorDictionary[pn] : null;
	if(!color) {
		var newColor = seriesColor.getColorForPoint(pn);
		if(newColor) {
			color = newColor;
		} else {
			color = colors[colorIndex];
			colorDictionary[pn] = color;
			colorIndex++;
			if(colorIndex >= colors.length) {
				colorIndex = 0;
			}
			yield put(seriesColorActions.newPoint(pn, color));
		}
	}


	return chart.addSeries({
		type: "line",
		name: pointName.formatDescription(view),
		yAxis: getAxisIndex(chart, symbol ? symbol : view.units),
		data: [],
		marker: {
			enabled: false
		},
		color: color,
		step: isSteppedSeries ? "left" : undefined,
		tooltip: {
			pointFormatter: function() {
				return (
					'<span style="color:{point.color}">\u25CF</span> ' +
					this.series.name +
					": <b>" +
					number.parseNumber(this.y) +
					" " +
					view.units +
					"</b><br/>"
				);
			}
		}
	});
}

//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units) {
	if (!chart.options) {
		return;
	}
	let axisIndex = 0;
	//if the first yaxis doesnt have a value set then
	if (!chart.yAxis[0].userOptions.title) {
		chart.yAxis[0].update({
			title: {
				text: units,
				align: "high",
				rotation: 0,
				margin: 0,
				y: -30,
				offset: 14,
				style: {
					"font-weight": "600",
					"font-size": "15px"
				}
			},
			labels: {
				style: {
					fontSize: "14px",
					color: "#999"
				},
				formatter() {
					return number.parseNumber(this.value, 1).replace(/0*$|\.0*$|\.$/g, "");
				},
			}
		});
	} else {
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for (var ii = 0; ii < chart.yAxis.length; ii++) {
			let wkAxis = chart.yAxis[ii];
			if (wkAxis.userOptions.title.text == units) {
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if (!axisFound) {
			axisIndex = chart.yAxis.length;
			chart.addAxis({
				opposite: chart.yAxis.length % 2 !== 0,
				title: {
					text: units,
					align: "high",
					rotation: 0,
					margin: 0,
					y: -30,
					offset: 14,
					style: {
						"font-weight": "600",
						"font-size": "15px"
					}
				},
				labels: {
					style: {
						fontSize: "14px",
						color: "#999"
					},

					formatter() {
						return number.parseNumber(this.value, 1).replace(/0*$|\.0*$|\.$/g, "");
					},
				}
			});
		}
	}
	return axisIndex;
}

function* startLiveInterval(payload) {
	let { instance, currentSite } = yield select(x => ({
		instance: x.reportlivelinechart.instances[payload.id],
		currentSite: x.sitetree.currentSite
	}));

	if (!instance.chart || instance.running) {
		return;
	}
	let views = [];
	let series = [];
	let component = instance.rootView;
	if (component && component.inputs && component.inputs.length) {
		for (var ii = 0; ii < component.inputs.length; ii++) {
			let wkInput = component.inputs[ii];
			if (wkInput.values && wkInput.values.length) {
				for (var jj = 0; jj < wkInput.values.length; jj++) {
					let wkValue = wkInput.values[jj];
					let viewData = yield call(pointsservice.TrendableObjectGetObject, wkValue.value);
					if (viewData && viewData.objectID) {
						viewData.coV = wkValue.coV;
						views.push(viewData);
					}
				}
			}
		}
		if (!instance.chart || instance.running || !("axes" in instance.chart)) {
			return;
		}
		for (var ii = 0; ii < views.length; ii++) {
			let wkView = views[ii];
			series.push({
				view: wkView,
				series: yield call(addSeries, instance.chart, wkView)
			});
		}
		let onLoadCallbackCompleted = false;
		while (instance && instance.chart) {
			currentSite = yield select(x => x.sitetree.currentSite);
			if (!currentSite) {
				yield take(sitetreetypes.ON_LOAD);
				currentSite = yield select(x => x.sitetree.currentSite);
			}
			if (currentSite) {
				let utcOffset = 0;
				if (currentSite.momentTimezone) {
					utcOffset = moment()
						.tz(currentSite.momentTimezone)
						.utcOffset();
				}
				let now = moment()
					.utc()
					.add(utcOffset, "m");

				let chartFromDate = moment()
					.utc()
					.subtract(1, "h")
					.add(utcOffset, "m");
				let toDate = now;

				yield all([series.map(s => call(updateInstantaneousData, s, now))]);

				if (instance && instance.chart) {
					instance.chart.reflow();
					instance.chart.xAxis[0].setExtremes(chartFromDate.valueOf(), toDate.add(2, "m").valueOf());
				}
			}
			yield delay(LIVE_DATA_INTERVAL);
			instance = yield select(x => x.reportlivelinechart.instances[payload.id]);
			if(payload.onLoadComplete && !onLoadCallbackCompleted){
				payload.onLoadComplete();
				onLoadCallbackCompleted = true;
			}
		}
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([this.onStartLiveData()]);
	},
	*onStartLiveData() {
		yield takeEvery(types.START_LIVE_DATA, function*({ payload }) {
			yield fork(startLiveInterval, payload);
		});
	}
};

////////
//actions
export const actions = {
	startLiveData(id, rootView, chart, onLoadComplete) {
		return {
			type: types.START_LIVE_DATA,
			payload: { id, rootView, chart, onLoadComplete }
		};
	},
	updateLiveData(id, data) {
		return {
			type: types.UPDATE_LIVE_DATA,
			payload: { id, data }
		};
	},
	stopLiveData(id) {
		return {
			type: types.STOP_LIVE_DATA,
			payload: { id }
		};
	},
	updateLastUpdated(id, lastUpdate) {
		return {
			type: types.UPDATE_LAST_UPDATED,
			payload: { id, lastUpdate }
		};
	}
};



// WEBPACK FOOTER //
// ./src/components/ReportComponents/Charts/ReportLiveLineChart/redux.js